import { useState, useEffect } from "react";
import { AjouterTemplatePopup } from "./AjouterTemplate";
import { ContratTemplate } from "../../model/Model";
import { 
    getTemplates, 
    supprimerTemplate, 
    ajouterTemplate, 
    modifierTemplate,
    dupliquerTemplate 
} from "../../service/templates/templates.service";

function ContratTemplates() {
    const [templates, setTemplates] = useState<ContratTemplate[]>([]);
    const [menuOpen, setMenuOpen] = useState<number | null>(null);
    const [seen, setSeen] = useState(false);
    const [templateId, setTemplateId] = useState<number | undefined>(undefined);

    useEffect(() => {
        async function fetchData() {
            const data = await getTemplates();
            console.log('Templates récupérés:', data);
            setTemplates(data);
        }
        fetchData();
    }, []);

    const handleMenuClick = (e: React.MouseEvent, id: number | undefined) => {
        e.stopPropagation();
        if (id !== undefined) {
            setMenuOpen(menuOpen === id ? null : id);
        }
    };

    function togglePopup() {
        setSeen(!seen);
        setTemplateId(undefined);
    }

    const closePopup = () => {
        setSeen(false);
        setTemplateId(undefined);
    };

    async function ajouterModifier(template: ContratTemplate): Promise<void> {
        try {
            if (template.id) {
                // Modifier un template existant
                await modifierTemplate(template);
            } else {
                // Ajouter un nouveau template
                await ajouterTemplate(template);
            }
            // Recharger la liste des templates
            const updatedTemplates = await getTemplates();
            setTemplates(updatedTemplates);
        } catch (error) {
            console.log(error);
            alert('Erreur lors de la sauvegarde du template');
        } finally {
            setSeen(false);
        }
    }

    const handleModifier = (id: number) => {
        setTemplateId(id);
        setSeen(true);
    };

    async function handleSupprimer(id: number) {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) {
            return;
        }
        try {
            await supprimerTemplate(id);
            const updatedTemplates = await getTemplates();
            setTemplates(updatedTemplates);
        } catch (error) {
            console.log(error);
            alert('Erreur lors de la suppression du template');
        }
    }

    async function handleDupliquer(id: number) {
        try {
            await dupliquerTemplate(id);
            const updatedTemplates = await getTemplates();
            setTemplates(updatedTemplates);
        } catch (error) {
            console.log(error);
            alert('Erreur lors de la duplication du template');
        }
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Non définie';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    useEffect(() => {
        const handleClickOutside = () => {
            if (menuOpen !== null) {
                setMenuOpen(null);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [menuOpen]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold text-gray-800">Templates de contrats</h2>
                    <p className="text-sm text-gray-600 mt-1">Gérez vos modèles de contrats réutilisables</p>
                </div>
                <button 
                    onClick={togglePopup}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                >
                    + Créer un template
                </button>
            </div>

            {seen && <AjouterTemplatePopup ajouterModifier={ajouterModifier} onClose={closePopup} templateId={templateId} />}

            {templates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((template) => (
                        <div 
                            key={template.id} 
                            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200"
                        >
                            <div className="p-5">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                            {template.nom}
                                        </h3>
                                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                            {template.type}
                                        </span>
                                    </div>
                                    <div className="relative inline-block text-left">
                                        <button 
                                            onClick={(e) => handleMenuClick(e, template.id)}
                                            className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                            title="Actions"
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                            </svg>
                                        </button>
                                        
                                        {menuOpen === template.id && (
                                            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                                <div className="py-1">
                                                    <button
                                                        className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors flex items-center"
                                                        onClick={() => {
                                                            setMenuOpen(null);
                                                            template.id && handleDupliquer(template.id);
                                                        }}
                                                    >
                                                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                        </svg>
                                                        Dupliquer
                                                    </button>
                                                    <button
                                                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors flex items-center"
                                                        onClick={() => {
                                                            setMenuOpen(null);
                                                            template.id && handleModifier(template.id);
                                                        }}
                                                    >
                                                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                        Modifier
                                                    </button>
                                                    <button
                                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                                                        onClick={() => {
                                                            setMenuOpen(null);
                                                            template.id && handleSupprimer(template.id);
                                                        }}
                                                    >
                                                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        Supprimer
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <p className="text-sm text-gray-600 mb-3">
                                    {template.description}
                                </p>
                                
                                {template.dateCreation && (
                                    <div className="text-xs text-gray-500 mt-auto pt-3 border-t border-gray-100">
                                        Créé le {formatDate(template.dateCreation)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-lg px-6 py-12 text-center">
                    <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun template trouvé</h3>
                    <p className="text-gray-500">Cliquez sur "Créer un template" pour créer votre premier modèle de contrat.</p>
                </div>
            )}
        </div>
    );
}

export default ContratTemplates;
